/**
 * LocationDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel with 3 tabs:
 * Config, Refine, Variants
 *
 * Time of day & weather are now in the Config tab's LocationDesigner flyout.
 *
 * Width: 400px. Image aspect: 16:9 (aspect-video).
 */

import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import { Position, NodeProps, useReactFlow, useStoreApi, Handle } from 'reactflow'
import { toast } from 'sonner'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import {
  createLocationPanorama,
  executeNode,
  extractLocationPanoramaViews,
  type LocationPanoramaExtractCount,
} from '../../../../api/client'
import { AnnotationEditorOverlay, useImageAnnotation } from '../shared/annotate'
import { useDuplicateMenuItems } from '../shared/useDuplicateMenuItems'
import { useExecuteNodeAction } from '../../../../hooks/useExecuteNodeAction'
import { generateModeFor } from '../../../../analytics/nodeAnalytics'
import { useBooleanFlagValue } from '../../../../hooks/useBooleanFlagValue'
import { useCanvasOptional } from '../../CanvasContext'
import { useActiveCanvasId } from '../../../../contexts/ActiveCanvasIdContext'
import { useBlockingOverlay } from '../../../../hooks/canvasShortcutGuard'
import { useCanvasEdges } from '../../CanvasEdgesContext'
import { useHandlesVisibleForNode } from '../../HandleVisibilityContext'
import { useCanvasNodeChromeMode } from '../../CanvasNodeChromeModeContext'
import { useCanvasNode } from '../../../../hooks/useCanvasNode'
import { getImageModelAlias } from '../../../../constants/modelAliases'
import { dataStatus,dataStrOpt, type ReactFlowCanvasEdgeData, ReactFlowCanvasNodeData } from '../../../../hooks/useYCanvas'
import { useSetHoveredNodeId } from '../../NodeHoverContext'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  QuickInfoBar,
  LOCATION_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
  IMAGE_MODEL_OPTIONS,
  localizeTimeOfDay,
  localizeWeather,
  RefineTabContent,
  VariantsTabContent,
  computeVariantPanelLayout,
  useDesignerVariantSelection,
  useVariantImageDownload,
  useNodeActions,
  useSaveToLibrary,
  ShimmerPlaceholder,
  useBottomPanelAutoOpen,
  useSelectSingleCanvasNode,
  createHandlesFromSchema,
  generatedImageHistoryToJson,
  getDesignerMainImage,
  getPickedIndex,
  resolveDesignerImagePath,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  FlatIdleNodeCard,
  useAssetNodeGeometryTrace,
  type HistoryEntry,
  computeBorderStyle,
} from '../shared'
import { useFeedbackMode, type RefineRunOptions } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { resolveIdentityBorderState, useNodeIdentityBorderTrace } from '../shared/useNodeIdentityBorderTrace'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createDebug, createNadDebug } from '../../../../utils/debugLog'
import { cn } from '../../../../lib/utils'
import { NODE_CARD_RADIUS_CLASS, NODE_CARD_INNER_RADIUS_CLASS } from '../shared/constants'
import {
  BookmarkPlus,
  Camera,
  Check,
  Copy,
  Download,
  Grid2X2,
  Grid3X3,
  Loader2,
  Maximize2,
  Palette,
  RotateCcw,
  ScanEye,
  Trash2,
} from 'lucide-react'
import { AddToChatIcon } from '../shared/AddToChatIcon'
import { Panorama720Icon } from '../shared/Panorama720Icon'
import {
  NODE_SCHEMAS_BY_TYPE,
  type ReactFlowCanvasNodeDataByType,
} from '../../../../generated/nodeDataTypes'
import {
  LocationPanoramaViewport,
  LocationPanoramaViewer,
} from './LocationPanoramaViewer'
import {
  PANORAMA_DEFAULT_VIEW,
  PANORAMA_SOURCE_IMAGE_SLOT,
  getLocationImageSlotPath,
  normalizePanoramaView,
} from './panoramaView'
import { useRegisterNodeContextAction } from '../../contextMenu/nodeContextActions'

export const TIME_OF_DAY_OPTIONS = ['dawn', 'day', 'dusk', 'night']
export const WEATHER_OPTIONS = ['clear', 'cloudy', 'rainy', 'foggy', 'snowy', 'stormy']

import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'
import { NodeStatusMessage } from '../shared/NodeStatusMessage'
import { NODE_BG_SWATCH_COLORS, resolveNodeBgColor, localizeNodeBgColor } from '../shared/nodeBgColors'

const locationDesignerSchema = NODE_SCHEMAS_BY_TYPE.locationDesigner
type LocationDesignerNodeData = ReactFlowCanvasNodeDataByType['locationDesigner']

// Card width (px) and the fixed variant-grid aspect (location cards are 16:9).
// Shared by all asset designer nodes (character/location/object) so they line
// up on the canvas -- keep in sync with schemas/nodes/location_designer.json.
const NODE_WIDTH = 400
const VARIANTS_ASPECT = '16/9'
const variantsPanelLog = createNadDebug('dw:variants-panel')
const descriptionStateDebug = createDebug('dw:canvas:designer-description')
const descriptionStateLog = createNadDebug('dw:canvas:designer-description')
const panoramaNodeLog = createNadDebug('dw:location-panorama-viewer')
const panoramaExtractNodesLog = createNadDebug('dw:location-panorama-extract-nodes')

const HANDLES = createHandlesFromSchema(locationDesignerSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_720_enabled: { disabled: true },
    in_720_view: { disabled: true },
  },
  outputs: {
    out_name: { position: Position.Right, style: { top: '35%' } },
    out_metadata: { position: Position.Right },
  },
})

const agentColor = AGENT_COLORS.location

const LocationDesignerNode = memo(function LocationDesignerNode(
  props: NodeProps<LocationDesignerNodeData>
) {
  const nodePresentationWidth = useNodePresentationWidth()
  const { t } = useTranslation('canvas')
  const { id, data, selected, dragging } = props
  const status = dataStatus(data)
  const error = dataStrOpt(data, 'error')
  const cacheKey = dataStrOpt(data, 'cacheKey')
  const progressMessage = dataStrOpt(data, 'progressMessage')
  const projectId = useProjectId()
  const activeCanvasId = useActiveCanvasId()
  const locationPanoramaEnabled = useBooleanFlagValue('location_panorama_enabled', false)

  const { setNodes, getNodes } = useReactFlow<ReactFlowCanvasNodeData, ReactFlowCanvasEdgeData>()
  const edges = useCanvasEdges()
  const handlesVisible = useHandlesVisibleForNode(id)
  const canvas = useCanvasOptional()
  const node = useCanvasNode(id, 'locationDesigner', data)

  const in_name = node?.get('in_name') ?? ''
  const in_style = node?.get('in_style')
  const in_time_of_day = node?.get('in_time_of_day') ?? 'day'
  const in_weather = node?.get('in_weather') ?? 'clear'
  const in_model = node?.get('in_model') ?? 'gemini-3.1-flash-image-preview'
  const imageModelLabel = getImageModelAlias(in_model)
    ?? IMAGE_MODEL_OPTIONS.find((o) => o.value === in_model)?.label
    ?? 'Model'
  const timeOfDayLabel = localizeTimeOfDay(in_time_of_day, t)
  const weatherLabel = localizeWeather(in_weather, t)
  const in_description = node?.get('in_description') ?? ''
  const out_image = node?.get('out_image')
  const dataOutImage = data.out_image
  const in_bg_color = node?.get('in_bg_color')
  const in_720_enabled = node?.get('in_720_enabled') ?? false
  const rawPanoramaView = node?.get('in_720_view') ?? data.in_720_view
  const rawPanoramaYaw = rawPanoramaView?.yaw_degrees
  const rawPanoramaPitch = rawPanoramaView?.pitch_degrees
  const rawPanoramaFov = rawPanoramaView?.fov_degrees
  const persistedPanoramaView = useMemo(
    () => normalizePanoramaView({
      yaw_degrees: rawPanoramaYaw,
      pitch_degrees: rawPanoramaPitch,
      fov_degrees: rawPanoramaFov,
    }),
    [rawPanoramaFov, rawPanoramaPitch, rawPanoramaYaw],
  )
  const [draftPanoramaView, setDraftPanoramaView] = useState(persistedPanoramaView)
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat } = useNodeActions({ nodeId: id })
  const duplicateMenuItems = useDuplicateMenuItems(handleDuplicate, in_name || t('locationDesignerNode.untitledLocation'))
  const { saveState, handleSaveToLibrary, saveLabel } = useSaveToLibrary({ nodeId: id, selected })
  const setHoveredNodeId = useSetHoveredNodeId()
  const { lightweightNodeChrome } = useCanvasNodeChromeMode()
  const [isHovered, setIsHovered] = useState(false)
  const [isHoveringLightweightNodeChrome, setIsHoveringLightweightNodeChrome] = useState(false)
  const [showColorPicker, setShowColorPicker] = useState(false)
  const colorPickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showColorPicker) return
    const handleClickOutside = (e: MouseEvent) => {
      if (colorPickerRef.current && e.target instanceof Node && !colorPickerRef.current.contains(e.target)) {
        setShowColorPicker(false)
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [showColorPicker])

  useEffect(() => {
    if (!selected) setShowColorPicker(false)
  }, [selected])

  // Image URL resolution
  const imagePath = resolveDesignerImagePath(out_image) ?? resolveDesignerImagePath(dataOutImage)
  const cacheBust = cacheKey
  const imageUrl =
    imagePath && projectId ? getThumbnailUrl(projectId, imagePath, 0, cacheBust) : null
  const fullImageUrl = imagePath && projectId ? getAssetUrl(projectId, imagePath, 0) : null
  const panoramaSourcePath = getLocationImageSlotPath(out_image, PANORAMA_SOURCE_IMAGE_SLOT)
    ?? getLocationImageSlotPath(dataOutImage, PANORAMA_SOURCE_IMAGE_SLOT)
  const panoramaSourceUrl = panoramaSourcePath && projectId
    ? getAssetUrl(projectId, panoramaSourcePath, 0, cacheBust)
    : null

  const isRunning = status === 'running'

  // NAD trace: prove this card's width matches its schema and that the preview
  // is 16:9 (post 1592855802471717). Filter with
  // `nad trace --feature asset-node-geometry`.
  useAssetNodeGeometryTrace({
    nodeId: id,
    nodeType: 'locationDesigner',
    componentWidth: NODE_WIDTH,
    schemaWidth: locationDesignerSchema.layout['*'].width,
    imageAspectClass: 'aspect-video',
    variantsAspect: VARIANTS_ASPECT,
  })
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'

  useEffect(() => {
    descriptionStateLog({
      event: 'designer-description:render-state',
      trace_id: `designer-description:${id}:${status}`,
      node_id: id,
      node_type: 'locationDesigner',
      status,
      description_state: showSpinner ? 'shimmer' : 'textarea',
      debug_enabled: descriptionStateDebug.enabled,
    })
  }, [id, showSpinner, status])

  useEffect(() => {
    setDraftPanoramaView((current) => {
      if (
        current.yaw_degrees === persistedPanoramaView.yaw_degrees &&
        current.pitch_degrees === persistedPanoramaView.pitch_degrees &&
        current.fov_degrees === persistedPanoramaView.fov_degrees
      ) {
        return current
      }
      return persistedPanoramaView
    })
  }, [persistedPanoramaView])

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  // Variants now open as a dedicated, tab-less panel via the quick-info bar
  // (post 1533007285123236) — filter it out of the config-mode tab bar.
  const tabs = useMemo(() => LOCATION_TABS.filter((tab) => tab.id !== 'variants'), [])
  const variantsMode = activeTab === 'variants'
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isPanoramaModalOpen, setIsPanoramaModalOpen] = useState(false)
  const [panoramaExtractingCount, setPanoramaExtractingCount] =
    useState<LocationPanoramaExtractCount | null>(null)
  const [panoramaCreating, setPanoramaCreating] = useState(false)
  // Suspend canvas keyboard shortcuts while this overlay is open (see canvasShortcutGuard).
  useBlockingOverlay(isModalOpen || isPanoramaModalOpen)
  const selectSingleNode = useSelectSingleCanvasNode()
  const flowStore = useStoreApi()
  const activateInlinePanorama = useCallback(() => {
    const previousSelectedIds = Array.from(flowStore.getState().nodeInternals.values())
      .filter((n) => n.selected).map((n) => n.id)
    selectSingleNode(id)
    if (previousSelectedIds.length !== 1 || previousSelectedIds[0] !== id) {
      panoramaNodeLog({
        event: 'panorama:node-selection',
        trace_id: `location-panorama-viewer:${id}:view`,
        feature: 'location-panorama-viewer',
        node_id: id,
        replaced_selection_node_ids: { ids: previousSelectedIds },
        selected_node_id: id,
        selection_changed: true,
        selection_mode: 'replace',
        surface: 'inline-node',
        trigger: 'primary-pointerdown',
        gesture_claimed_by: 'panorama-rotation',
      })
    }
  }, [flowStore, id, selectSingleNode])

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      selectSingleNode(id)
      variantsPanelLog({
        event: 'bottom-panel:open',
        trace_id: `variants-panel:${id}`,
        node_id: id,
        node_type: 'locationDesigner',
        mode: tab === 'variants' ? 'variants' : 'config',
        active_tab: tab,
      })
    },
    [id, selectSingleNode]
  )

  // History state
  const mainImageHistory = useMemo(
    () => {
      void cacheKey
      return getDesignerMainImage(out_image)
    },
    [out_image, cacheKey]
  )
  const historyEntries = useMemo(
    (): HistoryEntry[] => {
      void cacheKey
      return generatedImageHistoryToJson(mainImageHistory?.history)
    },
    [mainImageHistory, cacheKey]
  )
  const pickedIndex = getPickedIndex(mainImageHistory)
  const handleVariantDownload = useVariantImageDownload({ nodeId: id, history: historyEntries })
  const batchPending = showSpinner ? (node?.get('batch_pending') ?? 0) : 0

  // Dedicated Variants panel layout: tiles match the preview width; panel sized
  // for the grid (2 cols for 16:9) without scroll, grows then scrolls beyond.
  const variantLayout = useMemo(
    () =>
      computeVariantPanelLayout(
        VARIANTS_ASPECT,
        NODE_WIDTH,
        historyEntries.length + batchPending,
    ),
    [historyEntries.length, batchPending],
  )
  const panelContentHeight = variantsMode
    ? variantLayout.contentHeight
    : undefined

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    nodeType: 'locationDesigner',
    status,
  })

  // Shared variant selection (writes images.main.picked_index and derives out_image)
  const handleHistorySelect = useDesignerVariantSelection({
    nodeId: id,
    nodeType: 'locationDesigner',
    historyEntries,
    outputFieldName: 'out_image',
  })

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      node?.writeInDescription(newDesc)
    },
    [id, setNodes, node]
  )

  // Config generate handler — useExecuteNodeAction adds 1s cooldown
  // and routes insufficient_sparks 402 to the workspace modal.
  const { runExecute, isLocked: generateLocked, cancelExecution, isCancelling } = useExecuteNodeAction({
    nodeType: 'location_designer',
    projectId,
    nodeId: id,
    isRunning,
  })
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      if (showSpinner) return
      runExecute(retryCount, { analytics: { tab: 'config', generationMode: generateModeFor(historyEntries.length > 0) } })
    },
    [runExecute, showSpinner, historyEntries.length],
  )

  // Refine handler
  const onRefineSubmit = useCallback(
    (retryCount?: number, options?: RefineRunOptions) => {
      handleRefine(retryCount, options)
    },
    [handleRefine],
  )

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      node?.writeInStyle(styleId)
    },
    [id, setNodes, node]
  )

  // Time of day / weather change handlers for Config tab flyout
  const handleTimeOfDayChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_time_of_day: value } } : n
        )
      )
      node?.writeInTimeOfDay(value)
    },
    [id, setNodes, node]
  )

  const handleWeatherChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_weather: value } } : n
        )
      )
      node?.writeInWeather(value)
    },
    [id, setNodes, node]
  )

  // Model change handler
  const handleModelChange = useCallback(
    (model: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_model: model } } : n
        )
      )
      node?.writeInModel(model)
    },
    [id, setNodes, node]
  )

  const handlePanoramaViewCommit = useCallback(
    (view: typeof PANORAMA_DEFAULT_VIEW) => {
      const normalized = normalizePanoramaView(view)
      setDraftPanoramaView(normalized)
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_720_view: normalized } } : n
        )
      )
      const writeView = () => {
        node?.writeIn720View(normalized)
      }
      if (canvas?.transact) {
        canvas.transact(writeView)
      } else {
        writeView()
      }
      panoramaNodeLog({
        event: 'view-state-write',
        trace_id: `location-panorama-viewer:${id}:view`,
        feature: 'location-panorama-viewer',
        node_id: id,
        yaw_degrees: Math.round(normalized.yaw_degrees),
        pitch_degrees: Math.round(normalized.pitch_degrees),
        fov_degrees: Math.round(normalized.fov_degrees),
      })
    },
    [canvas, id, node, setNodes],
  )
  const canExtractPanoramaView = Boolean(
    locationPanoramaEnabled && in_720_enabled && panoramaSourceUrl && !showSpinner && projectId,
  )
  const handleResetPanoramaView = useCallback(() => {
    handlePanoramaViewCommit(PANORAMA_DEFAULT_VIEW)
    panoramaNodeLog({
      event: 'view-reset',
      trace_id: `location-panorama-viewer:${id}:view`,
      feature: 'location-panorama-viewer',
      node_id: id,
    })
  }, [handlePanoramaViewCommit, id])
  const handleExtractPanoramaView = useCallback(
    async (view = draftPanoramaView, count: LocationPanoramaExtractCount = 1) => {
      if (!canExtractPanoramaView || panoramaExtractingCount !== null || !projectId) return
      const next = normalizePanoramaView(view)
      handlePanoramaViewCommit(next)
      setPanoramaExtractingCount(count)
      panoramaExtractNodesLog({
        event: 'extract-request',
        trace_id: `location-panorama-extract-nodes:${id}:extract`,
        feature: 'location-panorama-extract-nodes',
        node_id: id,
        canvas_id: activeCanvasId,
        count,
        yaw_degrees: Math.round(next.yaw_degrees),
        pitch_degrees: Math.round(next.pitch_degrees),
        fov_degrees: Math.round(next.fov_degrees),
      })
      try {
        const result = await extractLocationPanoramaViews(projectId, id, {
          canvasId: activeCanvasId,
          view: next,
          count,
        })
        toast.success(t('locationDesignerNode.panoramaExtractSuccess', {
          count: result.created_nodes.length,
        }))
        panoramaExtractNodesLog({
          event: 'extract-success',
          trace_id: `location-panorama-extract-nodes:${id}:extract`,
          feature: 'location-panorama-extract-nodes',
          node_id: id,
          canvas_id: activeCanvasId,
          count: result.created_nodes.length,
          created_node_ids: result.created_nodes.map((created) => created.node_id),
        })
      } catch (error) {
        const message = error instanceof Error
          ? error.message
          : t('locationDesignerNode.panoramaExtractFailed')
        toast.error(message)
        panoramaExtractNodesLog({
          event: 'extract-error',
          trace_id: `location-panorama-extract-nodes:${id}:extract`,
          feature: 'location-panorama-extract-nodes',
          node_id: id,
          canvas_id: activeCanvasId,
          count,
          error: message,
        })
      } finally {
        setPanoramaExtractingCount(null)
      }
    },
    [
      activeCanvasId,
      canExtractPanoramaView,
      draftPanoramaView,
      handlePanoramaViewCommit,
      id,
      panoramaExtractingCount,
      projectId,
      t,
    ],
  )

  const handleCreatePanoramaNode = useCallback(async () => {
    if (
      !locationPanoramaEnabled ||
      in_720_enabled ||
      !imagePath ||
      !projectId ||
      panoramaCreating
    ) {
      panoramaNodeLog({
        event: 'create-node-skipped',
        trace_id: `location-panorama-viewer:${id}:create`,
        feature: 'location-panorama-viewer',
        node_id: id,
        reason: !locationPanoramaEnabled
          ? 'flag-disabled'
          : in_720_enabled
              ? 'already-panorama'
              : !imagePath
                ? 'missing-source-image'
                : !projectId
                  ? 'missing-project'
                  : 'already-creating',
      })
      return
    }

    const nextView = normalizePanoramaView(PANORAMA_DEFAULT_VIEW)
    const sourceName = in_name.trim() || t('locationDesignerNode.untitledLocation')
    const panoramaName = `${sourceName} 720`
    setPanoramaCreating(true)
    panoramaNodeLog({
      event: 'create-node-request',
      trace_id: `location-panorama-viewer:${id}:create`,
      feature: 'location-panorama-viewer',
      source_node_id: id,
      canvas_id: activeCanvasId,
    })
    try {
      const result = await createLocationPanorama(projectId, id, {
        canvasId: activeCanvasId,
        view: nextView,
        name: panoramaName,
        run: false,
      })
      try {
        await executeNode(projectId, result.node_id, { canvasId: activeCanvasId })
      } catch (runError) {
        const runMessage = runError instanceof Error ? runError.message : String(runError)
        toast.error(t('locationDesignerNode.panoramaRunFailed', { message: runMessage }))
        panoramaNodeLog({
          event: 'create-node-run-error',
          trace_id: `location-panorama-viewer:${id}:create`,
          feature: 'location-panorama-viewer',
          source_node_id: id,
          new_node_id: result.node_id,
          canvas_id: activeCanvasId,
          error: runMessage,
        })
        return
      }
      panoramaNodeLog({
        event: 'source-node-created',
        trace_id: `location-panorama-viewer:${id}:create`,
        feature: 'location-panorama-viewer',
        source_node_id: id,
        new_node_id: result.node_id,
        from_port: 'out_image',
        to_port: 'in_images',
        model_source: 'backend_panorama_contract',
        status: result.status,
      })
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : t('locationDesignerNode.panoramaCreateFailed')
      toast.error(message)
      panoramaNodeLog({
        event: 'create-node-error',
        trace_id: `location-panorama-viewer:${id}:create`,
        feature: 'location-panorama-viewer',
        source_node_id: id,
        canvas_id: activeCanvasId,
        error: message,
      })
    } finally {
      setPanoramaCreating(false)
    }
  }, [
    activeCanvasId,
    id,
    imagePath,
    in_720_enabled,
    in_name,
    locationPanoramaEnabled,
    panoramaCreating,
    projectId,
    t,
  ])

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [id, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      node?.writeInDescription(value)
    },
    [id, setNodes, node]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      node?.writeInName(newName)
    },
    [id, setNodes, node],
  )

  const handleBgColorChange = useCallback(
    (colorKey: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_bg_color: colorKey } } : n,
        ),
      )
      node?.writeInBgColor(colorKey)
      setShowColorPicker(false)
    },
    [id, setNodes, node],
  )

  // Download handler — standardized filename via shared helper.
  const onDownload = useCallback(() => {
    const downloadPath =
      locationPanoramaEnabled && in_720_enabled && panoramaSourcePath
        ? panoramaSourcePath
        : imagePath
    if (!downloadPath) return
    const { filename } = buildAssetDownloadFilename({
      canvasNodes: getNodes(),
      canvasEdges: edges,
      nodeId: id,
      assetPath: downloadPath,
      mediaType: 'image',
      variantHistory: historyEntries,
      pickedIndex,
    })
    handleDownload(downloadPath, filename)
  }, [edges, getNodes, handleDownload, historyEntries, id, imagePath, in_720_enabled, locationPanoramaEnabled, panoramaSourcePath, pickedIndex])
  useRegisterNodeContextAction(
    id,
    'download',
    onDownload,
    Boolean((locationPanoramaEnabled && in_720_enabled && panoramaSourcePath) || imagePath),
  )

  const handleOpenFullscreen = useCallback(() => {
    if (locationPanoramaEnabled && in_720_enabled && panoramaSourceUrl) {
      setIsPanoramaModalOpen(true)
      return
    }
    if (fullImageUrl) {
      setIsModalOpen(true)
    }
  }, [fullImageUrl, in_720_enabled, locationPanoramaEnabled, panoramaSourceUrl])

  // RFC-1115: annotate the full-resolution asset, not the thumbnail —

  // the bake reads the original.

  const annotate = useImageAnnotation({

    nodeId: id,

    feNodeType: 'locationDesigner',

    imagePath,

    imageUrl: fullImageUrl,

    projectId,

  })


  const downloadLabel = locationPanoramaEnabled && in_720_enabled && panoramaSourcePath
    ? t('locationDesignerNode.panoramaDownloadSource')
    : t('locationDesignerNode.download')

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'bg-color',
        label: t('locationDesignerNode.backgroundColor'),
        icon: <Palette size={14} />,
        onClick: () => setShowColorPicker((prev) => !prev),
      },
      {
        key: 'create-panorama',
        label: t('locationDesignerNode.createPanorama'),
        icon: panoramaCreating
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <Panorama720Icon className="w-4 h-4" />,
        onClick: handleCreatePanoramaNode,
        hidden: !locationPanoramaEnabled || in_720_enabled || !imagePath,
        disabled: showSpinner || generateLocked || panoramaCreating,
      },
      {
        key: 'fullscreen',
        label: t('locationDesignerNode.expand'),
        icon: <Maximize2 className="w-4 h-4" />,
        onClick: handleOpenFullscreen,
        hidden: !imagePath && !panoramaSourceUrl,
        disabled: showSpinner,
      },
      {
        key: 'download',
        label: downloadLabel,
        icon: <Download className="w-4 h-4" />,
        onClick: onDownload,
        hidden: !imagePath && !panoramaSourcePath,
        disabled: showSpinner,
      },
      {
        key: 'panorama-reset',
        label: t('locationDesignerNode.panoramaReset'),
        icon: <RotateCcw className="w-4 h-4" />,
        onClick: handleResetPanoramaView,
        hidden: !locationPanoramaEnabled || !in_720_enabled || !panoramaSourceUrl,
        disabled: showSpinner,
      },
      {
        key: 'panorama-extract',
        label: t('locationDesignerNode.panoramaExtractViews'),
        icon: panoramaExtractingCount !== null
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <ScanEye className="w-4 h-4" />,
        hidden: !locationPanoramaEnabled || !in_720_enabled || !panoramaSourceUrl,
        disabled: !canExtractPanoramaView || panoramaExtractingCount !== null,
        menuItems: [
          {
            key: 'current-view',
            label: t('locationDesignerNode.panoramaUseView'),
            icon: <Camera className="w-4 h-4" />,
            onClick: () => handleExtractPanoramaView(draftPanoramaView, 1),
            disabled: !canExtractPanoramaView || panoramaExtractingCount !== null,
          },
          {
            key: 'four-angles',
            label: t('locationDesignerNode.panoramaExtract4Views'),
            icon: <Grid2X2 className="w-4 h-4" />,
            onClick: () => handleExtractPanoramaView(draftPanoramaView, 4),
            disabled: !canExtractPanoramaView || panoramaExtractingCount !== null,
          },
          {
            key: 'eight-angles',
            label: t('locationDesignerNode.panoramaExtract8Views'),
            icon: <Grid3X3 className="w-4 h-4" />,
            onClick: () => handleExtractPanoramaView(draftPanoramaView, 8),
            disabled: !canExtractPanoramaView || panoramaExtractingCount !== null,
          },
        ],
        group: true,
      },
      {
        key: 'addToChat',
        label: t('locationDesignerNode.addToChat'),
        icon: <AddToChatIcon size={14} />,
        onClick: handleAddToChat,
        group: true,
      },
      {
        key: 'save-to-library',
        label: saveLabel,
        icon:
          saveState === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : saveState === 'success' ? (
            <Check className="w-4 h-4 text-green-400" />
          ) : (
            <BookmarkPlus className="w-4 h-4" />
          ),
        onClick: handleSaveToLibrary,
        disabled: saveState !== 'idle',
        group: true,
      },
      annotate.toolbarAction,
      {
        key: 'duplicate',
        label: t('locationDesignerNode.duplicate'),
        icon: <Copy className="w-4 h-4" />,
        menuItems: duplicateMenuItems,
        group: true,
      },
      {
        key: 'delete',
        label: t('locationDesignerNode.delete'),
        icon: <Trash2 className="w-4 h-4" />,
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [
      duplicateMenuItems,
      handleDelete,
      handleAddToChat,
      handleSaveToLibrary,
      saveState,
      saveLabel,
      t,
      annotate.toolbarAction,
      canExtractPanoramaView,
      generateLocked,
      handleCreatePanoramaNode,
      handleExtractPanoramaView,
      handleOpenFullscreen,
      handleResetPanoramaView,
      imagePath,
      in_720_enabled,
      locationPanoramaEnabled,
      panoramaExtractingCount,
      panoramaSourcePath,
      panoramaSourceUrl,
      panoramaCreating,
      showSpinner,
      draftPanoramaView,
      onDownload,
      downloadLabel,
    ]
  )

  // Border color state machine (shared) — custom node color (in_bg_color) drives
  // the idle/hover border; selected/running keep agent identity, error stays
  // destructive. Replaces the previous inline copy (which lacked the error branch)
  // so all designer nodes share one source of truth.
  const computedBorderStyle = computeBorderStyle(!!selected, status, isHovered, agentColor, in_bg_color)

  // Card side of the shared identity-border trace (post 1579940090429955).
  // Computed inline here rather than via DesignerNodeShell, so it needs its own
  // emitter for the trace to cover all three designer types.
  useNodeIdentityBorderTrace({
    nodeId: id,
    nodeType: 'locationDesigner',
    surface: 'card',
    agentColor,
    appliedBorderColor: computedBorderStyle.borderColor,
    borderState: resolveIdentityBorderState(!!selected, status, isHovered),
  })

  // Panel visibility
  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  const shouldRenderFlatNode = lightweightNodeChrome && !selected && !dragging &&
    !isHoveringLightweightNodeChrome && !isModalOpen && !isPanoramaModalOpen && !annotate.isOpen

  if (shouldRenderFlatNode) {
    return (
      <div
        data-testid="location-designer-lightweight-host"
        className="w-fit"
        onPointerEnter={() => { setIsHoveringLightweightNodeChrome(true); setIsHovered(true); setHoveredNodeId(id) }}
        onPointerLeave={() => { setIsHoveringLightweightNodeChrome(false); setIsHovered(false); setHoveredNodeId(null) }}
      >
        <FlatIdleNodeCard
          nodeId={id}
          testId="location-designer-flat-node"
          label={in_name}
          fallbackLabel={t('locationDesignerNode.altLocation')}
          width={NODE_WIDTH}
          status={status}
          error={error ? sanitizeNodeError(error, t, true) : undefined}
          progressMessage={progressMessage}
          handles={HANDLES}
          mediaKind={imageUrl ? 'image' : 'none'}
          mediaUrl={imageUrl}
          outputVariantIndex={pickedIndex}
          aspectClassName="aspect-video"
          placeholderLabel={t('locationDesignerNode.noLocationYet')}
          compactHandles
          backgroundColor={resolveNodeBgColor(in_bg_color)}
        />
      </div>
    )
  }

  return (
    <>
      <div
        className="relative"
        onPointerEnter={lightweightNodeChrome ? () => { setIsHoveringLightweightNodeChrome(true); setIsHovered(true); setHoveredNodeId(id) } : undefined}
        onPointerLeave={lightweightNodeChrome ? () => { setIsHoveringLightweightNodeChrome(false); setIsHovered(false); setHoveredNodeId(null) } : undefined}
      >
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* RFC-1115 annotation editor. Mounted at the NODE ROOT, not inside the
            image container: that container clips (`overflow: hidden`), which cuts
            off the tool palette — wider than the node card — and leaves its
            buttons unclickable behind the ReactFlow pane. */}
        {annotate.isOpen && fullImageUrl && (
          <AnnotationEditorOverlay
            imageUrl={fullImageUrl}
            altText={t('locationDesignerNode.altLocation')}
            traceId={annotate.traceId}
            nodeId={id}
            isSaving={annotate.isSaving}
            onCancel={annotate.close}
            onSave={annotate.save}
          />
        )}

        {/* Floating label above the card */}
        <FloatingNodeLabel name={in_name} fallback={t('locationDesignerNode.untitledLocation')} typeIcon={NODE_TYPE_ICON.locationDesigner} onRename={handleRename} bgColor={in_bg_color && in_bg_color !== 'default' ? NODE_BG_SWATCH_COLORS[in_bg_color] : undefined} />

        {/* Color picker panel */}
        {showColorPicker && (
          <div
            ref={colorPickerRef}
            data-testid="location-color-picker"
            style={{
              position: 'absolute',
              top: -52,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 60,
            }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-1.5 rounded-full bg-surface-2 backdrop-blur-md border border-border shadow-xl px-3 py-2">
              {Object.entries(NODE_BG_SWATCH_COLORS).map(([key, swatchColor]) => {
                const isActive = (in_bg_color ?? 'default') === key
                return (
                  <button
                    key={key}
                    aria-label={localizeNodeBgColor(key, t)}
                    title={localizeNodeBgColor(key, t)}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleBgColorChange(key)
                    }}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: swatchColor,
                      border: isActive ? '2px solid white' : '2px solid transparent',
                      cursor: 'pointer',
                      flexShrink: 0,
                      outline: 'none',
                      boxShadow: isActive ? '0 0 0 1px rgba(255,255,255,0.3)' : 'none',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                    }}
                  />
                )
              })}
            </div>
          </div>
        )}

        {/* Main Card -- 400px, matching the other asset designer nodes */}
        <div
          data-testid="location-designer-node"
          className={cn(
            'canvas-node-card-contained relative border transition-all duration-200 overflow-hidden cursor-pointer',
            NODE_CARD_RADIUS_CLASS,
            showSpinner && 'animate-node-shimmer'
          )}
          style={{ width: nodePresentationWidth(NODE_WIDTH), backgroundColor: resolveNodeBgColor(in_bg_color), ...computedBorderStyle }}
          onMouseEnter={() => {
            setIsHovered(true)
            setHoveredNodeId(id)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
            setHoveredNodeId(null)
          }}
        >
          {/* ReactFlow Handles */}
          {handlesVisible && HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Image Area */}
          <div className="relative group p-1.5">
            {/* RFC-1115 annotation editor. `inset-1.5` matches the container's
                padding so the editable surface lines up with the image. */}

            {locationPanoramaEnabled && in_720_enabled && panoramaSourceUrl && !showSpinner ? (
              <LocationPanoramaViewport
                imageUrl={panoramaSourceUrl}
                view={draftPanoramaView}
                nodeId={id}
                onDraftChange={setDraftPanoramaView}
                onCommit={handlePanoramaViewCommit}
                onActivate={activateInlinePanorama}
                className={cn(
                  'w-full aspect-video',
                  NODE_CARD_INNER_RADIUS_CLASS,
                )}
                testId="location-panorama-inline-canvas"
              />
            ) : imageUrl && (
              <img
                src={imageUrl}
                alt={t('locationDesignerNode.altLocation')}
                decoding="async"
                className={cn(
                  'w-full aspect-video object-cover block',
                  NODE_CARD_INNER_RADIUS_CLASS,
                  showSpinner && 'blur-sm brightness-50 transition-all duration-300'
                )}
              />
            )}

            {showSpinner && imageUrl && (
              <div className={`absolute inset-1.5 ${NODE_CARD_INNER_RADIUS_CLASS} flex flex-col items-center justify-center`}>
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full ${spinClass}`}
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-sm text-white mt-3 font-medium">
                  {isRunning
                    ? t('locationDesignerNode.refiningLocation')
                    : isPendingCost
                      ? t('locationDesignerNode.waitingForCostApproval')
                      : t('locationDesignerNode.queuedWaiting')}
                </span>
                {isRunning && (
                  <button
                    onClick={(e) => { e.stopPropagation(); cancelExecution() }}
                    disabled={isCancelling}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                    data-testid="cancel-node-button"
                    title={t('locationDesignerNode.stop')}
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    <span className="text-xxs font-medium">{t('locationDesignerNode.stop')}</span>
                  </button>
                )}
              </div>
            )}

            {!imageUrl && showSpinner && (
              <div className={`w-full aspect-video bg-surface-1/50 ${NODE_CARD_INNER_RADIUS_CLASS} flex flex-col items-center justify-center`}>
                <div
                  className={`w-5 h-5 border-2 border-t-transparent rounded-full ${spinClass}`}
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-xxs text-text-muted mt-3 text-center">
                  {progressMessage ||
                    (isRunning
                      ? (data.in_name ? t('locationDesignerNode.creatingLocationName', { name: data.in_name }) : t('locationDesignerNode.creatingLocation'))
                      : isPendingCost
                        ? t('locationDesignerNode.waitingForCostApproval')
                        : t('locationDesignerNode.queuedWaiting'))}
                </span>
                {isRunning && (
                  <button
                    onClick={(e) => { e.stopPropagation(); cancelExecution() }}
                    disabled={isCancelling}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                    data-testid="cancel-node-button"
                    title={t('locationDesignerNode.stop')}
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    <span className="text-xxs font-medium">{t('locationDesignerNode.stop')}</span>
                  </button>
                )}
              </div>
            )}

            {!imageUrl && !showSpinner && (
              <div className={`w-full aspect-video bg-surface-1/50 ${NODE_CARD_INNER_RADIUS_CLASS} flex flex-col items-center justify-center`}>
                <svg
                  className="w-10 h-10 text-text-muted/20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xxs text-text-muted/40 mt-1">{t('locationDesignerNode.noLocationYet')}</span>
              </div>
            )}

            {/* Expand / download on hover, matching the character and object
                cards (DesignerNodeShell). Same handlers as the toolbar, so a
                720 node opens the panorama viewer and downloads the source. */}
            {(imageUrl || panoramaSourceUrl) && !showSpinner && (
              <div
                className="absolute top-[14px] right-[14px] flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                data-testid="location-hover-actions"
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOpenFullscreen()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title={t('locationDesignerNode.expand')}
                  aria-label={t('locationDesignerNode.expand')}
                  data-testid="location-hover-expand"
                >
                  <Maximize2 className="w-4 h-4" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title={downloadLabel}
                  aria-label={downloadLabel}
                  data-testid="location-hover-download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            )}

            {in_720_enabled && !showSpinner && (
              <div
                className="absolute left-[14px] top-[14px] rounded-full border border-white/20 bg-black/60 px-2 py-1 text-xxs font-medium text-white backdrop-blur-sm"
                data-testid="location-panorama-badge"
              >
                {t('locationDesignerNode.panorama720')}
              </div>
            )}

            {/* Status overlay — floats over the bottom of the image box (inset
                to match the container padding) instead of adding a footer, so a
                failure never changes the card's outline size (post
                1634117708345526). */}
            {status === 'error' && error && (
              <NodeStatusMessage
                message={sanitizeNodeError(error, t, true)}
                testId="designer-card-error"
                className="inset-x-1.5 bottom-1.5 rounded-b-sm"
              />
            )}
          </div>

          {/* Content Area */}
          <div className="pt-2 px-6 pb-6">
            <span className="text-xs font-medium" style={{ color: agentColor.active }}>
              {t('locationDesignerNode.altLocation')}
            </span>

            {/* Description -- editable */}
            <div className="mt-2 h-[60px]">
              {showSpinner ? (
                <div className="space-y-1.5">
                  <ShimmerPlaceholder width="100%" height={14} />
                  <ShimmerPlaceholder width="80%" height={14} />
                </div>
              ) : (
                <textarea
                  data-testid="location-description-input"
                  aria-label={t('locationDesignerNode.locationDescription')}
                  value={in_description || ''}
                  placeholder={t('locationDesignerNode.describeLocation')}
                  onChange={handleDescriptionChange}
                  className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
                  rows={3}
                />
              )}
            </div>
          </div>

          {/* Quick Info Bar -- 2 columns: Config | Variants */}
          <QuickInfoBar
            presentation="designer"
            configSummary={in_720_enabled
              ? `${timeOfDayLabel} · ${weatherLabel} · ${t('locationDesignerNode.panorama720')}`
              : `${imageModelLabel} · ${timeOfDayLabel} · ${weatherLabel}`}
            references={currentRefs}
            projectId={projectId}
            cacheBust={cacheBust}
            variantCount={historyEntries.length}
            accentColor={agentColor.active}
            // Locations preview three references where the other designers show
            // two — a location's look is driven by more of them at once.
            maxReferences={3}
            logSource="location-designer"
            onOpenConfig={() => openPanelToTab('config')}
            onOpenVariants={() => openPanelToTab('variants')}
          />
        </div>

        {/* Unified Bottom Panel */}
        <UnifiedBottomPanel
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
          hideTabBar={variantsMode}
          // Every tab is as wide as the Variants panel. Config and Refine
          // previously fell back to `calc(100% + 120px)` — 520px against a
          // 400px node, versus 832px for Variants — which left them cramped
          // and made switching tabs resize the panel under the cursor.
          // Height still varies: the Variants height is derived from the tile
          // count and collapses to 150px when empty, which would make Config
          // SMALLER, so the other tabs keep the fixed default.
          minWidth={variantLayout.minWidth}
          contentHeight={panelContentHeight}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="location"
                  prompt={in_description}
                  onPromptChange={handlePromptChange}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={handleStyleChange}
                  model={in_model}
                  onModelChange={handleModelChange}
                  timeOfDay={in_time_of_day.charAt(0).toUpperCase() + in_time_of_day.slice(1)}
                  weather={in_weather.charAt(0).toUpperCase() + in_weather.slice(1)}
                  onTimeOfDayChange={(val) => handleTimeOfDayChange(val.toLowerCase())}
                  onWeatherChange={(w) => handleWeatherChange(w.toLowerCase())}
                  panoramaEnabled={in_720_enabled}
                  onCreatePanoramaNode={
                    locationPanoramaEnabled && !in_720_enabled
                      ? handleCreatePanoramaNode
                      : undefined
                  }
                  canCreatePanoramaNode={Boolean(
                    locationPanoramaEnabled && imagePath && !showSpinner && !generateLocked,
                  )}
                  isRunning={showSpinner || generateLocked}
                  onGenerate={handleConfigGenerate}
                  onCancel={isRunning ? cancelExecution : undefined}
                  isCancelling={isCancelling}
                  maxRetryCount={in_720_enabled ? 1 : undefined}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  nodeId={id}
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={onRefineSubmit}
                  isRunning={showSpinner}
                  nodeType="locationDesigner"
                  selectedStyleId={in_style}
                  onStyleChange={handleStyleChange}
                  onModelChange={in_720_enabled ? undefined : handleModelChange}
                  model={in_720_enabled ? undefined : in_model}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  targetBackendType="location_designer"
annotationSourcePath={imagePath}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  onDownloadVariant={handleVariantDownload}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  nodeId={id}
                  nodeType="locationDesigner"
                  agentColor={agentColor}
                  aspectRatio={VARIANTS_ASPECT}
                  maxWidth={NODE_WIDTH}
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {isPanoramaModalOpen && panoramaSourceUrl &&
        createPortal(
          <LocationPanoramaViewer
            imageUrl={panoramaSourceUrl}
            view={draftPanoramaView}
            nodeId={id}
            onDraftChange={setDraftPanoramaView}
            onCommit={handlePanoramaViewCommit}
            onClose={() => setIsPanoramaModalOpen(false)}
          />,
          document.body,
        )}

      {/* Fullscreen Lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt={t('locationDesignerNode.altFullSize')}
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

LocationDesignerNode.displayName = 'LocationDesignerNode'
export default LocationDesignerNode
import { useNodePresentationWidth } from '../../NodePresentationContext'
